// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleContainer } from './article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaUsageVO, MetaElementUsageIdentityVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ContainerService } from './container.service';
import { ConnectionInfoUtils } from './util/connection-info-utils';
import { ArticleService } from '@article/service/article.service';
import { ConnectionAreaUsageUtils } from '@shared/util/connection-area-usage-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';

@Injectable({ providedIn: 'root' })
export class ArticleConnectionService {
  constructor(
    private containerService: ContainerService,
    private articleService: ArticleService
  ) {}

  public isArticleConnectedToMountingRail(
    container: ArticleContainer,
    rangeOnLineType: string = null,
    considerOnlyConfiguredArticles: boolean = true,
    considerRangeOnLineType: boolean = true
  ): boolean {
    return this.isArticleConnectedToRangeOnLineArticleWithConfigured(
      container,
      null,
      rangeOnLineType,
      considerOnlyConfiguredArticles,
      considerRangeOnLineType,
      ArticleTypeConstants.TYPE_MOUNTING_RAIL
    );
  }

  public isArticleConnectedToRangeOnLineArticleWithConfigured(
    container: ArticleContainer,
    parentArticleUsageId: string = null,
    rangeOnLineType: string = null,
    considerOnlyConfiguredArticles: boolean = true,
    considerRangeOnLineType: boolean = true,
    rangeOnLineArticleType: string = null
  ): boolean {
    const isArticleConnected: boolean = this.isArticleConnectedToRangeOnLineArticle(
      container,
      parentArticleUsageId,
      rangeOnLineType,
      considerRangeOnLineType,
      rangeOnLineArticleType
    );
    if (isArticleConnected) {
      return considerOnlyConfiguredArticles ? ArticleUtils.isArticleConfigured(container.article) : true;
    } else {
      return false;
    }
  }

  public isArticleConnectedToRangeOnLineArticle(
    container: ArticleContainer,
    parentArticleUsageId: string = null,
    rangeOnLineType: string = null,
    considerRangeOnLineType: boolean = true,
    rangeOnLineArticleType: string = null
  ): boolean {
    if (!container) {
      return false;
    }
    const parentConnectionUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(container.articleUsage);
    // assumed that article can only be connected to single range-on-line (freedom) connection
    if (parentConnectionUsageIds.length !== 1) {
      return false;
    }
    const parentConnectionId: MetaElementUsageIdentityVO = parentConnectionUsageIds[0];
    const parentContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentConnectionId.articleUsageId);
    if (!parentContainer || !this.articleService.isRangeOnLineArticle(parentContainer.article, rangeOnLineArticleType)) {
      return false;
    }
    if (parentArticleUsageId && parentContainer.articleGUID !== parentArticleUsageId) {
      return false;
    }
    if (considerRangeOnLineType) {
      const rangeOnLineConnection: MetaElementVO = ConnectionInfoUtils.findRangeOnLineConnectionByType(
        parentContainer.article.metaElements,
        rangeOnLineType
      );
      if (!rangeOnLineConnection || rangeOnLineConnection.name !== parentConnectionId.metaElementName) {
        return false;
      }
    }
    return true;
  }

  public isArticleConnectedToRangeOnLineArticleType(container: ArticleContainer, parentArticleType: string): boolean {
    return this.isArticleConnectedToRangeOnLineArticleWithConfigured(container, null, null, true, true, parentArticleType);
  }

  public getBackwardConnectedArticlesOnRailByConnectionArea(connectionAreaUsage: ConnectionAreaUsageVO): ArticleContainer[] {
    const backwardConnectedConnectionUsageIds = ConnectionAreaUsageUtils.backwardConnectedArticleUsageIds(connectionAreaUsage);
    return this.getBackwardConnectedArticlesOnRail(backwardConnectedConnectionUsageIds);
  }

  public getBackwardConnectedArticlesOnRail(backwardConnectedUsageIds: string[]): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    for (const articleGuid of backwardConnectedUsageIds) {
      const backwardContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleGuid);
      if (backwardContainer) {
        const isArticleOnRail: boolean = this.isArticleConnectedToMountingRail(backwardContainer);
        if (isArticleOnRail) {
          if (result.indexOf(backwardContainer) === -1) {
            result.push(backwardContainer);
          }
        } else {
          const parentBackwardConnectionUsageIds: string[] = this.getParentBackwardConnectedArticleUsageIds(backwardContainer.articleUsage);
          const parentBackwardConnectedArticlesOnRail: ArticleContainer[] = this.getBackwardConnectedArticlesOnRail(parentBackwardConnectionUsageIds);
          ArrayUtils.mergeArrays(result, parentBackwardConnectedArticlesOnRail);
        }
      }
    }
    return result;
  }

  // in flex - WagoArticleUsageUtils.getBackwardConnectedConnectionUsageIds()
  // unclear why this method was introduced in flex: can't usual getBackwardConnectedArticleUsageIds() be used here?
  // don't make public access to this method!
  private getParentBackwardConnectedArticleUsageIds(articleUsage: ArticleUsageVO): string[] {
    const articleUsageIds = ArticleUsageUtils.connectionAreaUsages(articleUsage)
      .flatMap(connectionAreaUsage => ConnectionAreaUsageUtils.backwardConnectedArticleUsageIds(connectionAreaUsage));
    return articleUsageIds;
  }
}
